<template>
  <footer class="bg-neutral-900 text-white">

    <!-- Main Footer -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

        <!-- Col 1 — Brand + About -->
        <div class="lg:col-span-1">
          <!-- Logo -->
          <router-link to="/" class="inline-flex items-center gap-2 mb-5">
            <img src="@/assets/logo.png" alt="Estate Rwanda" class="h-10 w-auto brightness-0 invert" />
          </router-link>

          <p class="text-neutral-400 text-sm leading-relaxed mb-6">
            Your trusted partner in finding the perfect real estate in Rwanda. We help you buy, sell, and rent properties with confidence.
          </p>

          <!-- Address -->
          <div class="flex items-start gap-2 text-sm text-neutral-400 mb-4">
            <svg class="w-4 h-4 mt-0.5 flex-shrink-0 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            KG 567 St, Kiyovu, Kigali, Rwanda
          </div>

          <!-- Phone -->
          <a href="tel:+250789326245" class="flex items-center gap-2 text-sm text-neutral-400 hover:text-primary-400 transition-colors mb-2">
            <svg class="w-4 h-4 flex-shrink-0 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            +250 789 326 245
          </a>

          <!-- Email -->
          <a href="mailto:info@estaterwanda.com" class="flex items-center gap-2 text-sm text-neutral-400 hover:text-primary-400 transition-colors mb-5">
            <svg class="w-4 h-4 flex-shrink-0 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            info@estaterwanda.com
          </a>

          <!-- Social Icons -->
          <div class="flex items-center gap-3">
            <a href="https://www.facebook.com/profile.php?id=61576242489786" target="_blank" rel="noopener" aria-label="Facebook" class="w-8 h-8 bg-neutral-800 hover:bg-primary-500 rounded-lg flex items-center justify-center transition-colors">
              <svg class="w-4 h-4 text-neutral-400 hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a href="#" aria-label="Twitter / X" class="w-8 h-8 bg-neutral-800 hover:bg-primary-500 rounded-lg flex items-center justify-center transition-colors">
              <svg class="w-4 h-4 text-neutral-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
            </a>
            <a href="https://www.instagram.com/estaterwanda/" target="_blank" rel="noopener" aria-label="Instagram" class="w-8 h-8 bg-neutral-800 hover:bg-primary-500 rounded-lg flex items-center justify-center transition-colors">
              <svg class="w-4 h-4 text-neutral-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
              </svg>
            </a>
            <a href="#" aria-label="LinkedIn" class="w-8 h-8 bg-neutral-800 hover:bg-primary-500 rounded-lg flex items-center justify-center transition-colors">
              <svg class="w-4 h-4 text-neutral-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>

        <!-- Col 2 — Quick Links -->
        <div>
          <h4 class="text-sm font-bold text-white uppercase tracking-widest mb-5">Quick Links</h4>
          <ul class="space-y-3">
            <li v-for="link in quickLinks" :key="link.label">
              <router-link
                :to="link.to"
                class="flex items-center gap-2 text-sm text-neutral-400 hover:text-primary-400 transition-colors group"
              >
                <svg class="w-3 h-3 text-primary-500 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
                </svg>
                {{ link.label }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Col 3 — Popular Searches -->
        <div>
          <h4 class="text-sm font-bold text-white uppercase tracking-widest mb-5">Popular Searches</h4>
          <ul class="space-y-3">
            <li v-for="item in popularSearches" :key="item">
              <router-link
                to="/properties"
                class="flex items-center gap-2 text-sm text-neutral-400 hover:text-primary-400 transition-colors group"
              >
                <svg class="w-3 h-3 text-primary-500 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
                </svg>
                {{ item }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Col 4 — Newsletter -->
        <div>
          <h4 class="text-sm font-bold text-white uppercase tracking-widest mb-5">Newsletter</h4>
          <p class="text-neutral-400 text-sm leading-relaxed mb-5">
            Subscribe to get the latest listings, market trends, and property news delivered to your inbox.
          </p>
          <form @submit.prevent="subscribeNewsletter" class="flex flex-col gap-2">
            <input
              v-model="email"
              type="email"
              placeholder="Your email address"
              required
              class="w-full px-4 py-2.5 bg-neutral-800 border border-neutral-700 rounded-xl text-sm text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
            />
            <button
              type="submit"
              class="w-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors"
            >
              Subscribe
            </button>
          </form>

          <!-- Certifications / Trust badges -->
          <div class="mt-6 pt-5 border-t border-neutral-800">
            <p class="text-xs text-neutral-500 uppercase tracking-widest mb-3 font-semibold">Certified &amp; Trusted</p>
            <div class="flex items-center gap-2 flex-wrap">
              <span class="px-3 py-1 text-xs font-medium bg-neutral-800 text-neutral-400 rounded-full border border-neutral-700">RDB Registered</span>
              <span class="px-3 py-1 text-xs font-medium bg-neutral-800 text-neutral-400 rounded-full border border-neutral-700">RERA Member</span>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="border-t border-neutral-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p class="text-neutral-500 text-sm">
          &copy; {{ currentYear }} Estate Rwanda. All rights reserved.
        </p>
        <div class="flex items-center gap-5 text-sm">
          <a href="#" class="text-neutral-500 hover:text-primary-400 transition-colors">Privacy Policy</a>
          <a href="#" class="text-neutral-500 hover:text-primary-400 transition-colors">Terms of Service</a>
        </div>
      </div>
    </div>

  </footer>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'

@Options({})
export default class Footer extends Vue {
  email = ''
  currentYear = new Date().getFullYear()

  quickLinks = [
    { label: 'Home', to: '/' },
    { label: 'Properties', to: '/properties' },
    { label: 'About Us', to: '/about' },
    { label: 'Blog', to: '/blogs' },
    { label: 'Contact', to: '/contact' }
  ]

  popularSearches = [
    'Apartment for Rent',
    'House for Sale',
    'Office Space',
    'Shop for Rent',
    'Luxury Villas',
    'Land for Sale'
  ]

  subscribeNewsletter () {
    this.email = ''
  }
}
</script>
